import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { getQuotationStatusStyles } from 'src/app/shared/utils/quotation-status-styles';
import { ITimelineOrder, ITimelineOrderGroup } from '../../../models/timeline.models';
import {
  isMissingCustomerValue,
  orderCurrencyCode,
  orderCurrencySymbol,
} from '../../../utils/order-display.util';
import { ORDER_REFRESH_ACTIONS, refreshedOrderId } from '../../../utils/order-refresh.util';

/**
 * One row of the grouped artifact — an order, flattened to what the table prints
 * so the template holds no derivation logic.
 */
export interface IOrderGroupRow {
  order: ITimelineOrder;
  reference: string;
  customerName: string;
  isCustomerMissing: boolean;
  status: string;
  /** Null when the order carries no resolvable amount — the cell prints `--`. */
  amount: number | null;
  /**
   * Per row, not per card: `currency` hangs off each order, and the AI resolves it
   * from that payload's own location (`ai_quotation_service.create_from_payload`),
   * so one run can land two orders in two currencies.
   */
  currencySymbol: string;
  /** WB-7024 semantics — print 'No price' instead of a number. */
  allLinesNoPrice: boolean;
  /** Why this order was split off, or null for an AI multi-order PDF split. */
  splitReason: string | null;
  /**
   * Resolved once per row rather than from the template: `getQuotationStatusStyles`
   * returns a fresh object literal, so calling it in `[ngStyle]` would hand
   * `NgStyle` a new identity for every row on every change-detection pass.
   */
  statusStyles: { [key: string]: string };
}

/**
 * The "N Orders created" artifact (WB-7598): the orders one create-order run
 * produced, collapsed into a single timeline card.
 *
 * Covers both split kinds with one component, because both link every order they
 * produce to the same message — an AI multi-order PDF (WB-7372) and a platform
 * segregation rule (WB-7566). The grouping itself happens upstream in
 * `groupTimelineOrders`; this component only renders a group it is handed.
 *
 * Deliberately reuses the `.order-artifact` shell of the single-order card so the
 * two read as one family: same container, header rhythm, table and footer. Only
 * the table's subject changes — orders here, line items there.
 */
@Component({
  selector: 'app-order-group-artifact',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatTooltipModule,
    // SharedModule exposes the `daysAgoOrLeft` pipe used by the header timestamp.
    SharedModule,
    SvgIconComponent,
  ],
  templateUrl: './order-group-artifact.component.html',
  styleUrls: ['./order-group-artifact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderGroupArtifactComponent implements OnDestroy {
  // ── Inputs / Outputs ────────────────────────────────────────────────────────

  /**
   * Everything the card prints is derived here, once per group, rather than from
   * template getters: the whole view is a pure function of this input, and a
   * getter would re-run it on every change-detection pass — `hidePricing` and
   * `currencySymbol` are read inside the row loop, so that is O(N²) order visits
   * per pass for a card that never changes between inputs.
   */
  @Input()
  set group(value: ITimelineOrderGroup | null) {
    this._group = value;
    const orders = value?.orders ?? [];

    this.rows = orders.map((order) => this.toRow(order));
    this.orderCount = orders.length;
    // WB-7569 — one member hiding pricing hides it for the whole card. Erring the
    // other way would print, in the group total, the very number that member's
    // tenant setting exists to withhold.
    this.hidePricing = orders.some((o) => !!o.order.hide_pricing_columns);
    this.currencyCode = orderCurrencyCode(orders[0]?.order);
    // A total is only a number when every member is denominated in the same
    // thing. Empty names are ignored rather than counted as a distinct currency:
    // an order that carries none is a gap in the payload, not a second currency.
    this.mixedCurrency =
      new Set(orders.map((o) => o.order.currency?.name).filter(Boolean)).size > 1;
    // The most recent touch across the group: the run is one object to the user,
    // so the header answers "when was any of this last modified?".
    this.lastModifiedAt = orders.reduce<string | undefined>((latest, o) => {
      const stamp = o.order.last_modified ?? o.order.created_at;
      return stamp && (!latest || stamp > latest) ? stamp : latest;
    }, undefined);
    // Rows with no resolvable amount contribute nothing.
    this.total = this.rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
    // WB-7024, as the single card does in its own footer: a run where nothing is
    // priced totals 0, and printing `EUR 0.00` under rows that all read "No price"
    // states a price the order does not have. A partially priced run keeps the
    // number — that is the existing behaviour and the right one.
    this.allNoPrice = this.rows.length > 0 && this.rows.every((row) => row.allLinesNoPrice);
  }
  get group(): ITimelineOrderGroup | null {
    return this._group;
  }

  /** Open one order of the group in the side-by-side panel (row click). */
  @Output() openOrder = new EventEmitter<ITimelineOrder>();
  /** A member order changed — the parent should refetch the thread. */
  @Output() orderChanged = new EventEmitter<ITimelineOrder>();

  // ── Public properties ───────────────────────────────────────────────────────

  rows: IOrderGroupRow[] = [];
  orderCount = 0;
  /** ISO code prefixing the group total, e.g. `EUR 336,94` — as on the single card. */
  currencyCode = '';
  /** Members span two or more currencies: the rows stand, the group total cannot. */
  mixedCurrency = false;
  lastModifiedAt?: string;
  hidePricing = false;
  total = 0;
  /** Every member is unpriced — the footer prints 'No price', not `0.00`. */
  allNoPrice = false;

  // ── Private properties ──────────────────────────────────────────────────────

  private _group: ITimelineOrderGroup | null = null;
  private destroy$ = new Subject<void>();

  // ── Constructor ─────────────────────────────────────────────────────────────

  constructor(private translate: TranslateService, private actions$: Actions) {
    // Same contract as the single card, widened to the whole group: any member
    // mutating makes this card's rendered copy stale, and only a thread refetch
    // can put it right. Filtered to members so a card never reacts to an order
    // it does not show.
    this.actions$
      .pipe(ofType(...ORDER_REFRESH_ACTIONS), takeUntil(this.destroy$))
      .subscribe((action) => {
        const changedId = refreshedOrderId(action);
        const member = this._group?.orders.find((o) => o.order.id === changedId);
        if (member) this.orderChanged.emit(member);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  /**
   * Keyed on the order id so a rebuilt group reuses the existing row DOM instead
   * of tearing it down and back up. Anything asynchronous inside a row would
   * otherwise never finish — the split-reason icon in particular, whose SVG the
   * registry fetches over HTTP, was destroyed before its response landed and
   * rendered as an empty `<mat-icon>` forever.
   */
  trackByRow(_index: number, row: IOrderGroupRow): number {
    return row.order.order.id;
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Same destination as clicking a single order card: the side-by-side panel. */
  onOpenOrder(row: IOrderGroupRow, event?: MouseEvent): void {
    event?.stopPropagation();
    this.openOrder.emit(row.order);
  }

  // ── Private methods ─────────────────────────────────────────────────────────

  private toRow(timelineOrder: ITimelineOrder): IOrderGroupRow {
    const { order } = timelineOrder;
    const customerName = this.resolveCustomerName(order);

    return {
      order: timelineOrder,
      reference: order.name,
      customerName,
      isCustomerMissing: isMissingCustomerValue(customerName),
      status: order.state,
      // WB-7024: `priced_lines_total` is the total the order detail prints — it
      // sums only the priced lines, where `total` sums every one of them.
      amount: order.priced_lines_total ?? order.total ?? null,
      currencySymbol: orderCurrencySymbol(order),
      allLinesNoPrice: !!order.all_lines_no_price,
      splitReason: this.resolveSplitReason(timelineOrder),
      statusStyles: getQuotationStatusStyles(order.state),
    };
  }

  /**
   * The order's own company, falling back to the thread payload's display name.
   *
   * Unlike the single card this never waits on `GET /quotations/{id}`: a group of
   * 17 orders would mean 17 requests to fill one column, and the inline fields
   * already answer it.
   */
  private resolveCustomerName(order: ITimelineOrder['order']): string {
    const company = order.company_name?.trim();
    if (company && !isMissingCustomerValue(company)) return company;
    return order.customer_name?.trim() ?? '';
  }

  /**
   * What the row's info icon explains. `segregation_split_label` is the label the
   * split stamped on this very order; `segregation_info` answers the same question
   * for a child the rule cloned out — `split_label` is the human-facing reason
   * ("Brand: Acme"), `mechanism_name` names the rule that produced it and stands
   * in when no label was set.
   *
   * The order of the two matters: a `split_in_place` run stamps the label on every
   * order but never gives the source a `segregation_source_id`, so reading
   * `segregation_info` first would leave the source row of a group unbadged while
   * its siblings carry one.
   *
   * Null leaves the icon off the row entirely — an AI PDF split has no provenance
   * to show, and the header already says these orders came out of one run.
   */
  private resolveSplitReason(timelineOrder: ITimelineOrder): string | null {
    const info = timelineOrder.segregation_info;
    const label =
      timelineOrder.segregation_split_label?.trim() ||
      info?.split_label?.trim() ||
      info?.mechanism_name?.trim();
    if (!label) return null;

    return this.translate.instant('smart_inbox.order_group_artifact.split_reason', { reason: label });
  }
}
